import { IconButton, mergeStyles, TooltipHost } from "@fluentui/react";
import { getNextColor, IChartProps } from "@fluentui/react-charting";
import { useId } from "@fluentui/react-hooks";
import { Triangle12Filled, TriangleDown12Filled } from "@fluentui/react-icons";
import React, { useLayoutEffect, useRef, useState } from "react";
import { LineChartComponent } from "../../../ChartComponents/LineChartFluent";
import { PartnerCarouselProps } from "../../../interfaces/CarouselProps.interfaces";
import { IChartVariances } from "../../../interfaces/chart-props.interfaces";
import { IProductKPI } from "../../../interfaces/customer-product-v2.interfaces";
import CustomCard from "../../../SharedComponents/CustomCard";
import { CUSTOMER_VIEW_BREAKPOINTS } from "../../CustomerView/CustomerView.constants";
import * as styles from "./PartnerProductTelemetryChart.styles";
import ExploreFeaturesPanel from "../../../SharedComponents/ExploreFeaturesPanel/ExploreFeaturesPanel";
import { useGetPartnerFeaturesDataQuery } from "../../../slices/ExplorePartners.slice";

const breakpoints = CUSTOMER_VIEW_BREAKPOINTS;

export const PartnerProductTelemetryChart: React.FC<PartnerCarouselProps> = ({
  carouseldata,
  granularity,
  application,
  selectedFamily,
  partnerOneId,
  mpnId,
  disableExploreFeaturesButton = false
}) => {
  const tooltipId = useId("tooltip");
  const [cardWidth, setCardWidth] = useState<number | undefined>();
  const containerRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, error } = useGetPartnerFeaturesDataQuery({ partnerOneID: partnerOneId, mpnIdentifier: mpnId, productFamily: selectedFamily });

  useLayoutEffect(() => {
    const calculateWidth = () => {
      let itemsPerRow = 1;

      for (const bp of breakpoints) {
        if (window.innerWidth >= bp.width) {
          itemsPerRow = bp.items;
          break;
        }
      }

      if (containerRef.current) {
        const availableWidth = containerRef.current.offsetWidth;
        const newCardWidth = Math.floor(availableWidth / itemsPerRow) - 16;
        setCardWidth(newCardWidth);
      }
    };

    calculateWidth();
    window.addEventListener("resize", calculateWidth);
    return () => window.removeEventListener("resize", calculateWidth);
  }, []);

  const CardVariance = ({ variances }: { variances: IChartVariances[] }) => {
    const rootStyle = variances.length < 2 ? styles.rootVarianceStyleSingle : styles.rootVarianceStyle;

    return (
      <div style={{ ...rootStyle, width: cardWidth ?? 0 }}>
        {variances.map((v, idx) => (
          <div key={`cvd-${idx}`} className={mergeStyles(styles.varianceContainerStyle)}>
            <div
              style={{
                alignItems: "center",
                borderBottom: "1px solid #d5d5d580",
                color: getNextColor(idx),
                display: "flex",
                fontWeight: "bold",
                marginBottom: 2
              }}
            >
              <div className={mergeStyles(styles.varianceMetricNameStyle)} title={v.name}>
                {v.name}
              </div>
              <TooltipHost content={v.description} id={tooltipId}>
                <IconButton iconProps={{ iconName: "Info" }} aria-label="Info" />
              </TooltipHost>
            </div>
            <div>This month (till date)</div>
            <div style={styles.currMonthValStyle}>{v.variance.nextValue || "0"}</div>
            <div style={v.variance.diffValue.includes("-") ? styles.varianceRedStyle : styles.varianceGreenStyle}>
              {v.variance.diffValue.includes("-") ? (
                <TriangleDown12Filled />
              ) : v.variance.diffValue !== "0" ? (
                <Triangle12Filled />
              ) : null}
              <div>
                {v.variance.diffValue || 0} ({v.variance.diffPerc || "0%"})
              </div>
            </div>
            <div>v/s previous month ({v.variance.prevValue || 0})</div>
          </div>
        ))}
      </div>
    );
  };

  const GetItemData = (item: IProductKPI, idx: number) => {
    const filteredLineChartData =
      item.data?.map((metric, i) => {
        const filteredPoints =
          metric.data?.filter((point) => {
            const isMatchGranularity = [
              "Microsoft Purview Consumption",
              "Microsoft Sentinel",
              "Security Copilot",
              "Microsoft Defender for Cloud"
            ].includes(selectedFamily ?? "")
              ? point.aggregationType === granularity
              : true;
            const matchApp =
              ["Microsoft Purview Consumption"].includes(selectedFamily ?? "") && application
                ? point.application === application
                : true;
            return isMatchGranularity && matchApp;
          }) || [];

        return {
          legend: `${metric.name}`,
          data: filteredPoints.map((point) => ({
            x: new Date(point.x),
            y: Number(point.y)
          })),
          color: getNextColor(i),
          lineOptions: {
            lineBorderWidth: "4"
          }
        };
      }) ?? [];

    const allDataPoints = filteredLineChartData.flatMap((line) => line.data);
    if (allDataPoints.length === 0) return null;

    const chartData: IChartProps = {
      lineChartData: filteredLineChartData
    };
    const chartVariances: IChartVariances[] = item.data
      .filter((metric) => {
        const hasMonthlyData = metric.data?.some((point) => {
          const isMatchGranularity = [
            "Microsoft Purview Consumption",
            "Microsoft Sentinel",
            "Security Copilot",
            "Microsoft Defender for Cloud"
          ].includes(selectedFamily ?? "")
            ? point.aggregationType === "Monthly"
            : true;

          const matchApp =
            ["Microsoft Purview Consumption"].includes(selectedFamily ?? "") && application
              ? point.application === application
              : true;

          return isMatchGranularity && matchApp;
        });

        return hasMonthlyData;
      })
      .map(({ description, name, variance }) => ({
        description,
        name,
        variance
      }));

    return (
      <div ref={containerRef} style={styles.cardContainerStyle(cardWidth)} key={idx}>
        <CustomCard
          cardHeaderTitle={item.name}
          exploreFeaturesComponent={
            !disableExploreFeaturesButton && (
              <ExploreFeaturesPanel
                productFamily={selectedFamily ?? ""}
                productName={item.name}
                customProps={{
                  pageName: "Partner Page",
                  tabName: "Partner Product Telemetry",
                  apiName: "useGetPartnerFeaturesDataQuery"
                }}
                idProps={{ tenantId: "", partnerOneId: partnerOneId, mpnId: mpnId }}
                data={data}
                isLoading={isLoading}
                error={error}
              />
            )
          }
          kustoLink={item.kustoLink}
        >
          <CardVariance key={`CardVariance${idx}`} variances={chartVariances} />
          <div style={{ backgroundColor: "white" }}>
            <LineChartComponent data={chartData} height={300} width={cardWidth} allowResize={true} />
          </div>
        </CustomCard>
      </div>
    );
  };

  return (
    <div ref={containerRef} style={styles.containerStyle}>
      {carouseldata.items.map((item, idx) => GetItemData(item, idx)).filter(Boolean)}
    </div>
  );
};
